import { useRef, useEffect, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { MAPBOX_TOKEN } from '../../../../../../config/constants'

// Set MapBox access token
mapboxgl.accessToken = MAPBOX_TOKEN

export type MapViewType = 'streets' | 'satellite'

// Map styles for different view types
export const MAP_STYLES: Record<MapViewType, string> = {
  streets: 'mapbox://styles/mapbox/light-v11',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
}

export interface UseMapboxOptions {
  containerRef: React.RefObject<HTMLDivElement | null>
  center?: [number, number]
  zoom?: number
  initialStyle?: MapViewType
  isPrint?: boolean
  onLoad?: (map: mapboxgl.Map) => void
  onStyleLoad?: (map: mapboxgl.Map) => void
}

export interface UseMapboxReturn {
  map: React.MutableRefObject<mapboxgl.Map | null>
  isLoaded: boolean
  mapViewType: MapViewType
  setMapViewType: (style: MapViewType) => void
}

/**
 * Custom hook for managing Mapbox map instances in PDF export
 * Handles initialization, cleanup, and style switching
 */
export const useMapbox = ({
  containerRef,
  center = [8.304620661, 46.813258144], // Switzerland center
  zoom = 18,
  initialStyle = 'streets',
  isPrint = false,
  onLoad,
  onStyleLoad,
}: UseMapboxOptions): UseMapboxReturn => {
  const map = useRef<mapboxgl.Map | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [mapViewType, setMapViewTypeState] = useState<MapViewType>(initialStyle)

  // Store callbacks in refs to avoid recreating the map when they change
  const onLoadRef = useRef(onLoad)
  const onStyleLoadRef = useRef(onStyleLoad)

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onLoadRef.current = onLoad
    onStyleLoadRef.current = onStyleLoad
  }, [onLoad, onStyleLoad])

  // Handle style change
  const setMapViewType = useCallback((newStyle: MapViewType) => {
    setMapViewTypeState(newStyle)
    if (map.current) {
      map.current.setStyle(MAP_STYLES[newStyle])
    }
  }, [])

  // Initialize map when component mounts
  useEffect(() => {
    // Skip map initialization for print view or if container not ready
    if (isPrint || !containerRef.current || !mapboxgl.supported()) {
      return
    }

    // Don't recreate if map already exists
    if (map.current) {
      return
    }

    // Initialize map with current view type style
    map.current = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLES[mapViewType],
      center,
      zoom,
      preserveDrawingBuffer: true, // Required for print/PDF capture
    })

    // Handle initial load
    map.current.on('load', () => {
      setIsLoaded(true)
      if (onLoadRef.current && map.current) {
        onLoadRef.current(map.current)
      }
    })

    // Handle style changes (for re-adding features after style switch)
    map.current.on('style.load', () => {
      if (onStyleLoadRef.current && map.current) {
        onStyleLoadRef.current(map.current)
      }
    })

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
        setIsLoaded(false)
      }
    }
  }, [isPrint, containerRef, mapViewType, center, zoom])

  return {
    map,
    isLoaded,
    mapViewType,
    setMapViewType,
  }
}

export default useMapbox
